import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { ListFilter, Upload, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import PaginatorCst from "@components/PaginatorCst";
import InputCst from "@components/InputCst/InputCst";
import FiltersAccordionCst from "@components/FiltersAccordionCst";
import { PagesID } from "@utils/utilsConfigurations";
import { Search as SearchIcon } from "lucide-react";
import { Media } from "src/interfaces/Media";
import { useMediaList } from "./hooks/useMediaList";
import { useMediaUpload } from "./hooks/useMediaUpload";
import MediaPickerCard from "./components/MediaPickerCard";
import { MediaLibraryPickerProps, MediaPickerItem } from "./types";
import { ButtonPrimary } from "src/UI/ButtonPrimary";
import { ButtonSecondary } from "src/UI/ButtonSecondary";
import { ButtonTertiary } from "src/UI/ButtonTertiary";
import { PERMISSIONS, usePermissions } from "@utils/permissions";

const PAGE_SIZE = 16;
// Ricerca nascosta: si usano i filtri canonici (accordion). Rimettere true per riattivarla.
const MEDIA_SEARCH_ENABLED = false;

const toItem = (m: Media): MediaPickerItem => ({
  id: m.id,
  label: m.metadata?.name || `Media #${m.id}`,
});

const MediaLibraryPickerCst: React.FC<MediaLibraryPickerProps> = (props) => {
  const { open, onClose, title, mediaType, learningResourceType } = props;
  const { t } = useTranslation("media-library-picker");
  const theme = useTheme();
  const { id: corporateId = "" } = useParams();
  // Il caricamento crea un nuovo media nella libreria: serve media.write.
  const { can } = usePermissions();
  const canUploadMedia = can(PERMISSIONS.media.write);

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [searchInput, setSearchInput] = useState("");
  const [searchSubmitted, setSearchSubmitted] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [fieldFilters, setFieldFilters] = useState<Record<string, any>>({});

  const { items, totalItems, totalPages, isLoading, mutate } = useMediaList({
    corporateId,
    mediaType,
    learningResourceType,
    page,
    pageSize,
    search: searchSubmitted || undefined,
    fieldFilters,
    enabled: open,
  });

  const handleUploaded = (item: MediaPickerItem) => {
    setPage(1);
    mutate();
    if (props.multiple) return;
    props.onConfirm(item);
    onClose();
  };

  const { inputProps, openFileDialog, isUploading, uploadError } =
    useMediaUpload({
      corporateId,
      mediaType,
      learningResourceType,
      enabled: canUploadMedia,
      onUploaded: handleUploaded,
    });

  const [selected, setSelected] = useState<MediaPickerItem[]>([]);

  useEffect(() => {
    if (!open) return;
    if (props.multiple) {
      setSelected(props.initialSelection ?? []);
    } else {
      setSelected(props.initialSelection ? [props.initialSelection] : []);
    }
    setPage(1);
    setSearchInput("");
    setSearchSubmitted("");
  }, [open, props.multiple, props.initialSelection]);

  const selectedSet = useMemo(
    () => new Set(selected.map((s) => s.id)),
    [selected],
  );

  const handleCardClick = (item: Media) => {
    if (props.multiple) {
      setSelected((prev) => {
        const isSelected = prev.some((s) => s.id === item.id);
        if (isSelected) return prev.filter((s) => s.id !== item.id);
        return [...prev, toItem(item)];
      });
    } else {
      setSelected((prev) => {
        const isSelected = prev[0]?.id === item.id;
        return isSelected ? [] : [toItem(item)];
      });
    }
  };

  const handleSubmitSearch = () => {
    setSearchSubmitted(searchInput.trim());
    setPage(1);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSubmitSearch();
    }
  };

  const handleConfirm = () => {
    if (props.multiple) {
      props.onConfirm(selected);
    } else {
      props.onConfirm(selected[0] ?? null);
    }
    onClose();
  };

  const canConfirm = selected.length > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.default,
          borderRadius: "16px",
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "20px 24px",
          color: theme.palette.text.primary,
        }}
      >
        <Typography sx={{ fontSize: "20px", fontWeight: 700 }}>
          {title}
        </Typography>
        <IconButton
          onClick={onClose}
          aria-label="close"
          sx={{ color: theme.palette.text.primary }}
        >
          <X size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ padding: "12px 24px 16px" }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2.5,
          }}
        >
          <ButtonSecondary
            size="md"
            onClick={() => setFiltersOpen((prev) => !prev)}
            startIcon={<ListFilter size={16} />}
            sxButton={
              filtersOpen
                ? {
                    borderColor: theme.palette.systemSecondary01,
                  }
                : undefined
            }
          >
            {t("filters-button") || "Filtri"}
          </ButtonSecondary>

          {MEDIA_SEARCH_ENABLED && (
            <>
              <Box
                sx={{
                  flex: 1,
                  "& .MuiFormControl-root": {
                    minWidth: "0 !important",
                    width: "100% !important",
                  },
                  "& .MuiOutlinedInput-root": { borderRadius: "8px" },
                  "& .MuiFormHelperText-root": { display: "none" },
                  "& .MuiOutlinedInput-notchedOutline": { borderRadius: "8px" },
                }}
                onKeyDown={handleSearchKeyDown}
              >
                <InputCst
                  id="media-library-search"
                  placeholder={t("search-placeholder") || ""}
                  inputSearch
                  value={searchInput}
                  onChange={(e: any) => setSearchInput(e.target.value)}
                  startAdornment={
                    <SearchIcon
                      size={16}
                      color={theme.palette.systemSecondary01}
                      style={{ marginRight: 8 }}
                    />
                  }
                />
              </Box>
              <ButtonSecondary
                size="md"
                onClick={handleSubmitSearch}
                sx={{ flexShrink: 0 }}
              >
                {t("search-button")}
              </ButtonSecondary>
            </>
          )}

          <Box sx={{ flex: 1 }} />

          <ButtonTertiary
            size="md"
            onClick={openFileDialog}
            disabled={isUploading || !mediaType || !canUploadMedia}
            startIcon={
              isUploading ? (
                <CircularProgress size={14} />
              ) : (
                <Upload size={16} />
              )
            }
            sx={{ flexShrink: 0 }}
          >
            {isUploading ? t("uploading") : t("upload-new")}
          </ButtonTertiary>
          <input {...inputProps} />
        </Box>

        <Box sx={{ mb: filtersOpen ? 2.5 : 0 }}>
          <FiltersAccordionCst
            open={filtersOpen}
            onClose={() => setFiltersOpen(false)}
            pageId={PagesID["corporate.library.filters"]}
            value={fieldFilters}
            onApply={(next) => {
              setFieldFilters(next);
              setPage(1);
            }}
          />
        </Box>

        {uploadError && (
          <Box
            sx={{
              mb: 2,
              padding: "10px 14px",
              borderRadius: "8px",
              border: `1px solid ${theme.palette.error.main}`,
              backgroundColor: theme.palette.action.hover,
              color: theme.palette.error.main,
              fontSize: "13px",
            }}
          >
            {uploadError}
          </Box>
        )}

        {/* Grid 4 colonne (responsive) */}
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              py: 6,
              color: theme.palette.text.tertiary,
              fontSize: "13px",
            }}
          >
            {t("empty")}
          </Box>
        ) : (
          <Grid container spacing={2}>
            {items.map((item) => (
              <Grid key={item.id} item xs={12} sm={6} md={4} lg={3}>
                <MediaPickerCard
                  item={item}
                  selected={selectedSet.has(item.id)}
                  onClick={() => handleCardClick(item)}
                  corporateId={corporateId || "1"}
                />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              mt: 2,
            }}
          >
            <PaginatorCst
              page={page}
              pageSize={pageSize}
              totalItems={totalItems}
              onPageChange={setPage}
              onPageSizeChange={(next) => {
                setPageSize(next);
                setPage(1);
              }}
            />
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ padding: "16px 24px 20px", gap: 2 }}>
        <Box sx={{ flex: 1 }} />
        <ButtonPrimary size="md" onClick={handleConfirm} disabled={!canConfirm}>
          {t("confirm")}
        </ButtonPrimary>
      </DialogActions>
    </Dialog>
  );
};

export default MediaLibraryPickerCst;
